import { Injectable } from '@angular/core';

import { EntityState, EntityStore, QueryEntity, StoreConfig } from '@datorama/akita';

import { USER_MODULES_TYPE } from '@common/util/constants';

import { ModuleItem, ModuleState, ModuleStatus } from '../../common/types';

interface ModuleEntityState extends EntityState<ModuleItem & { _id: string }, string> {
  lastUpdate: {
    [key: string]: number; // Using format "state:status" as key
  };
  filters: {
    state: ModuleState;
    status: ModuleStatus;
    module_type: string;
  };
}

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'modules', idKey: '_id', resettable: true })
export class ModuleStore extends EntityStore<ModuleEntityState> {
  private CACHE_DURATION = 1 * 60 * 1000; // 1 minute in milliseconds

  constructor() {
    super({
      lastUpdate: {},
      filters: {
        state: ModuleState.Published,
        status: ModuleStatus.Active,
        module_type: 'all',
      },
    });
  }

  private getCacheKey(state: ModuleState, status: ModuleStatus): string {
    return `${state}:${status}`;
  }

  isCacheValid(state: ModuleState, status: ModuleStatus): boolean {
    const cacheKey = this.getCacheKey(state, status);
    const lastUpdate = this.getValue().lastUpdate?.[cacheKey] || 0;
    const now = Date.now();
    return now - lastUpdate < this.CACHE_DURATION;
  }

  updateLastFetch(state: ModuleState, status: ModuleStatus) {
    const cacheKey = this.getCacheKey(state, status);
    this.update(({ lastUpdate }) => ({
      lastUpdate: {
        ...lastUpdate,
        [cacheKey]: Date.now(),
      },
    }));
  }

  invalidateCache(combinations?: Array<{ state: ModuleState; status: ModuleStatus }>) {
    if (!combinations) {
      // If no combinations provided, invalidate all possible combinations
      const allCombinations = [
        { state: ModuleState.Published, status: ModuleStatus.Active },
        { state: ModuleState.Published, status: ModuleStatus.Inactive },
        { state: ModuleState.Draft, status: ModuleStatus.Active },
        { state: ModuleState.Draft, status: ModuleStatus.Inactive },
      ] as const;

      this.update((state) => ({
        ...state,
        lastUpdate: allCombinations.reduce(
          (acc, combo) => ({
            ...acc,
            [this.getCacheKey(combo.state, combo.status)]: 0,
          }),
          {},
        ),
      }));
    } else {
      this.update((state) => ({
        ...state,
        lastUpdate: {
          ...state.lastUpdate,
          ...combinations.reduce(
            (acc, combo) => ({
              ...acc,
              [this.getCacheKey(combo.state, combo.status)]: 0,
            }),
            {},
          ),
        },
      }));
    }
  }

  updateFilters(filters: Partial<ModuleEntityState['filters']>) {
    this.update((state) => ({
      ...state,
      filters: {
        ...state.filters,
        ...filters,
      },
    }));
  }
}

@Injectable({ providedIn: 'root' })
export class ModuleQuery extends QueryEntity<ModuleEntityState> {
  constructor(protected moduleState: ModuleStore) {
    super(moduleState);
  }

  filters$ = this.select((state) => state.filters);
  modules$ = this.select((state) => {
    const { state: moduleState, status, module_type } = state.filters;
    return this.getAll().filter(
      (module) =>
        module.state === moduleState &&
        module.status === status &&
        (module_type === 'all'
          ? true
          : module_type === USER_MODULES_TYPE
            ? !module.module_type
            : module.module_type === module_type),
    );
  });

  moduleTypes$ = this.select(() => {
    return [
      ...new Set(
        this.getAll()
          // .filter((module) => module.module_type)
          .map((module) => module.module_type || USER_MODULES_TYPE),
      ),
    ];
  });
}
